'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentProps, createContext, type ReactNode, useContext } from 'react';
import { Avatar, AvatarFallback } from './avatar';
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogHeaderClose, type DialogHeaderIcon, DialogTitle, DialogTrigger } from './dialog';
import { Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerHeaderClose, DrawerTitle, DrawerTrigger } from './drawer';
import { useIsMobile } from './hooks/use-mobile';
import { cn } from './lib/cn';
import type { TextProps } from './text';
interface BaseProps {
  children?: ReactNode;
}
interface RootCredenzaProps extends BaseProps {
  dismissible?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}
export interface CredenzaProps extends RootCredenzaProps {
  asChild?: true;
  className?: string;
}
const CredenzaContext = createContext<{
  isMobile: boolean;
}>({
  isMobile: false
});
const useCredenzaContext = () => {
  const context = useContext(CredenzaContext);
  if (!context) {
    throw new Error("Credenza components cannot be rendered outside the Credenza Context");
  }
  return context;
};
const Credenza = t0 => {
  const $ = _c(17);
  let children;
  let dismissible;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      dismissible,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = dismissible;
    $[3] = props;
  } else {
    children = $[1];
    dismissible = $[2];
    props = $[3];
  }
  const isMobile = useIsMobile();
  const Credenza = isMobile ? Drawer : Dialog;
  let t1;
  if ($[4] !== isMobile) {
    t1 = {
      isMobile
    };
    $[4] = isMobile;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== dismissible || $[7] !== isMobile) {
    t2 = isMobile ? {
      autoFocus: true,
      dismissible
    } : {};
    $[6] = dismissible;
    $[7] = isMobile;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== Credenza || $[10] !== children || $[11] !== props || $[12] !== t2) {
    t3 = <Credenza {...props} {...t2}>{children}</Credenza>;
    $[9] = Credenza;
    $[10] = children;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t3) {
    t4 = <CredenzaContext.Provider value={t1}>{t3}</CredenzaContext.Provider>;
    $[14] = t1;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
const CredenzaTrigger = t0 => {
  const $ = _c(9);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaTrigger = isMobile ? DrawerTrigger : DialogTrigger;
  let t1;
  if ($[4] !== CredenzaTrigger || $[5] !== children || $[6] !== className || $[7] !== props) {
    t1 = <CredenzaTrigger className={className} {...props}>{children}</CredenzaTrigger>;
    $[4] = CredenzaTrigger;
    $[5] = children;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
const CredenzaClose = t0 => {
  const $ = _c(9);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaClose = isMobile ? DrawerClose : DialogClose;
  let t1;
  if ($[4] !== CredenzaClose || $[5] !== children || $[6] !== className || $[7] !== props) {
    t1 = <CredenzaClose className={className} {...props}>{children}</CredenzaClose>;
    $[4] = CredenzaClose;
    $[5] = children;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
const CredenzaContent = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let overlayClassname;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      hideDrag: t1,
      overlayClassname,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = overlayClassname;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    overlayClassname = $[3];
    props = $[4];
    t1 = $[5];
  }
  const hideDrag = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaContent = isMobile ? DrawerContent : DialogContent;
  let t2;
  if ($[6] !== CredenzaContent || $[7] !== children || $[8] !== className || $[9] !== hideDrag || $[10] !== overlayClassname || $[11] !== props) {
    t2 = <CredenzaContent className={className} hideDrag={hideDrag} overlayClassname={overlayClassname} {...props}>{children}</CredenzaContent>;
    $[6] = CredenzaContent;
    $[7] = children;
    $[8] = className;
    $[9] = hideDrag;
    $[10] = overlayClassname;
    $[11] = props;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
const CredenzaHeader = t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      hideClose: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const hideClose = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaHeader = isMobile ? DrawerHeader : DialogHeader;
  let t2;
  if ($[5] !== CredenzaHeader || $[6] !== children || $[7] !== className || $[8] !== hideClose || $[9] !== props) {
    t2 = <CredenzaHeader className={className} hideClose={hideClose} {...props}>{children}</CredenzaHeader>;
    $[5] = CredenzaHeader;
    $[6] = children;
    $[7] = className;
    $[8] = hideClose;
    $[9] = props;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
const CredenzaHeaderIcon = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <Avatar size="48"><AvatarFallback size="48" variant="icon" {...props}>{children}</AvatarFallback></Avatar>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const CredenzaHeaderClose = t0 => {
  const $ = _c(7);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaHeaderClose = isMobile ? DrawerHeaderClose : DialogHeaderClose;
  let t1;
  if ($[3] !== CredenzaHeaderClose || $[4] !== className || $[5] !== props) {
    t1 = <CredenzaHeaderClose className={className} {...props} />;
    $[3] = CredenzaHeaderClose;
    $[4] = className;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const CredenzaTitle = t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaTitle = isMobile ? DrawerTitle : DialogTitle;
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-left font-inter font-medium text-lg text-role-on-background leading-6", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== CredenzaTitle || $[7] !== children || $[8] !== props || $[9] !== t1) {
    t2 = <CredenzaTitle className={t1} {...props}>{children}</CredenzaTitle>;
    $[6] = CredenzaTitle;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
const CredenzaDescription = t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaDescription = isMobile ? DrawerDescription : DialogDescription;
  let t1;
  if ($[4] !== className) {
    t1 = cn("font-inter text-base text-role-on-background leading-6 tracking-tight", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== CredenzaDescription || $[7] !== children || $[8] !== props || $[9] !== t1) {
    t2 = <CredenzaDescription className={t1} {...props}>{children}</CredenzaDescription>;
    $[6] = CredenzaDescription;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
const CredenzaBody = t0 => {
  const $ = _c(7);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaBody = isMobile ? DrawerBody : DialogBody;
  let t1;
  if ($[3] !== CredenzaBody || $[4] !== children || $[5] !== props) {
    t1 = <CredenzaBody {...props}>{children}</CredenzaBody>;
    $[3] = CredenzaBody;
    $[4] = children;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
const CredenzaFooter = t0 => {
  const $ = _c(7);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    isMobile
  } = useCredenzaContext();
  const CredenzaFooter = isMobile ? DrawerFooter : DialogFooter;
  let t1;
  if ($[3] !== CredenzaFooter || $[4] !== children || $[5] !== props) {
    t1 = <CredenzaFooter {...props}>{children}</CredenzaFooter>;
    $[3] = CredenzaFooter;
    $[4] = children;
    $[5] = props;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
export { Credenza, CredenzaBody, CredenzaClose, CredenzaContent, CredenzaDescription, CredenzaFooter, CredenzaHeader, CredenzaHeaderIcon, CredenzaTitle, CredenzaTrigger };